import toString from 'lodash/toString'
import uniq from 'lodash/uniq'

import * as priceApi from '../../../services/api/price'
import {
  PossiblyMixedValue,
  isMixedValue,
  percentageStringToFactorMax1,
  toNumberValue,
  toPossiblyMixedValue,
  toStringValue,
} from '../../utils/form'

import {Price, Provider} from '../../../types'

import {
  MixedFormData,
  PricingType,
  SingleFormData,
  StaggeredVolumePrice,
  VolumePrice,
  VolumePricingType,
} from './form'

// FormData Pricing to priceApi Pricing

const toRequestVolumePrice = (
  volumePrice: SingleFormData['volumePrice']
): priceApi.PriceRequest['volumePrice'] =>
  Array.isArray(volumePrice)
    ? (volumePrice as StaggeredVolumePrice[]).map(({volume, price}) => ({
        volume: toNumberValue(volume) as number,
        price: toNumberValue(price) as number,
      }))
    : toNumberValue(volumePrice)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formDataTransforms: Record<string, (value: any) => any> = {
  name: name => name,
  basePrice: toNumberValue,
  baseUnitPrice: toNumberValue,
  boundingBoxPrice: toNumberValue,
  density: toNumberValue,
  minimumPrice: toNumberValue,
  minimumUnitPrice: toNumberValue,
  quantityDiscount: toNumberValue,
  quantityDiscountCap: percentageStringToFactorMax1,
  surfacePrice: toNumberValue,
  volumePrice: toRequestVolumePrice,
  weightPrice: toNumberValue,
  useVolumeWithSupport: value => value,
}

export const formDataToPriceRequest = (
  formData: SingleFormData,
  provider: Provider
): priceApi.PriceRequest => ({
  name: formDataTransforms.name(formData.name),
  basePrice: formDataTransforms.basePrice(formData.basePrice),
  baseUnitPrice: formDataTransforms.baseUnitPrice(formData.baseUnitPrice),
  boundingBoxPrice: formDataTransforms.boundingBoxPrice(formData.boundingBoxPrice),
  density: formDataTransforms.density(formData.density),
  minimumPrice: formDataTransforms.minimumPrice(formData.minimumPrice),
  minimumUnitPrice: formDataTransforms.minimumUnitPrice(formData.minimumUnitPrice),
  quantityDiscount: formDataTransforms.quantityDiscount(formData.quantityDiscount),
  quantityDiscountCap: formDataTransforms.quantityDiscountCap(formData.quantityDiscountCap),
  surfacePrice: formDataTransforms.surfacePrice(formData.surfacePrice),
  volumePrice: formDataTransforms.volumePrice(formData.volumePrice),
  weightPrice: formDataTransforms.weightPrice(formData.weightPrice),
  useVolumeWithSupport: formDataTransforms.useVolumeWithSupport(formData.useVolumeWithSupport),
  provider: provider.id,
})

export const formDataToPartialPriceRequest = (formData: Partial<SingleFormData>) => {
  const priceRequest: Partial<priceApi.PriceRequest> = {}

  Object.keys(formData).forEach(key => {
    const value = formData[key as keyof SingleFormData]
    const priceRequestTransform = formDataTransforms[key]
    if (!isMixedValue(value) && priceRequestTransform) {
      priceRequest[key as keyof priceApi.PriceRequest] = priceRequestTransform(value)
    }
  })

  return priceRequest
}

// priceApi Pricing to FormData Pricing

const toVolumePrice = (value: Price['volumePrice']): VolumePrice =>
  Array.isArray(value)
    ? value.map(({volume, price}) => ({
        volume: toStringValue(volume),
        price: toStringValue(price),
      }))
    : toStringValue(value)

const toPricingType = <T>(
  pricings: Price[],
  determineType: (pricing: Price) => T
): PossiblyMixedValue<T> => {
  const types = pricings.map(determineType)
  const isMultipleTypes = uniq(types).length > 1
  return isMultipleTypes ? {mixed: true, values: types} : types[0]
}

export const pricingsToFormData = (pricings: Price[]): MixedFormData => ({
  name: toPossiblyMixedValue(
    pricings.map(pricing => pricing.name),
    toString
  ),
  basePrice: toPossiblyMixedValue(
    pricings.map(pricing => pricing.basePrice),
    toStringValue
  ),
  baseUnitPrice: toPossiblyMixedValue(
    pricings.map(pricing => pricing.baseUnitPrice),
    toStringValue
  ),
  boundingBoxPrice: toPossiblyMixedValue(
    pricings.map(pricing => pricing.boundingBoxPrice),
    toStringValue
  ),
  density: toPossiblyMixedValue(
    pricings.map(pricing => pricing.density),
    toStringValue
  ),
  minimumPrice: toPossiblyMixedValue(
    pricings.map(pricing => pricing.minimumPrice),
    toStringValue
  ),
  minimumUnitPrice: toPossiblyMixedValue(
    pricings.map(pricing => pricing.minimumUnitPrice),
    toStringValue
  ),
  quantityDiscount: toPossiblyMixedValue(
    pricings.map(pricing => pricing.quantityDiscount),
    toStringValue
  ),
  quantityDiscountCap: toPossiblyMixedValue(
    pricings.map(pricing =>
      pricing.quantityDiscountCap ? pricing.quantityDiscountCap * 100 : null
    ),
    toStringValue
  ),
  surfacePrice: toPossiblyMixedValue(
    pricings.map(pricing => pricing.surfacePrice),
    toStringValue
  ),
  volumePrice: toPossiblyMixedValue(
    pricings.map(pricing => pricing.volumePrice),
    toVolumePrice
  ),
  weightPrice: toPossiblyMixedValue(
    pricings.map(pricing => pricing.weightPrice),
    toStringValue
  ),
  pricingType: toPricingType<PricingType>(pricings, (pricing: Price) =>
    pricing.weightPrice ? PricingType.WEIGHT : PricingType.VOLUME
  ),
  volumePricingType: toPricingType<VolumePricingType>(pricings, (pricing: Price) =>
    Array.isArray(pricing.volumePrice) ? VolumePricingType.STAGGERED : VolumePricingType.SINGLE
  ),
  useVolumeWithSupport: toPossiblyMixedValue(
    pricings.map(pricing => pricing.useVolumeWithSupport),
    Boolean
  ),
})

export const pricingsToSingleFormData = (pricing: Price): SingleFormData => ({
  name: pricing.name,
  basePrice: toStringValue(pricing.basePrice),
  baseUnitPrice: toStringValue(pricing.baseUnitPrice),
  boundingBoxPrice: toStringValue(pricing.boundingBoxPrice),
  density: toStringValue(pricing.density),
  minimumPrice: toStringValue(pricing.minimumPrice),
  minimumUnitPrice: toStringValue(pricing.minimumUnitPrice),
  quantityDiscount: toStringValue(pricing.quantityDiscount),
  quantityDiscountCap: toStringValue(
    pricing.quantityDiscountCap ? pricing.quantityDiscountCap * 100 : null
  ),
  surfacePrice: toStringValue(pricing.surfacePrice),
  volumePrice: toVolumePrice(pricing.volumePrice),
  weightPrice: toStringValue(pricing.weightPrice),
  pricingType: pricing.weightPrice ? PricingType.WEIGHT : PricingType.VOLUME,
  volumePricingType: Array.isArray(pricing.volumePrice)
    ? VolumePricingType.STAGGERED
    : VolumePricingType.SINGLE,
  useVolumeWithSupport: pricing.useVolumeWithSupport,
})
